'use client'

import { useEffect, useRef } from 'react'

import { usePathname } from 'next/navigation'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { clearPersonalizationCache } from '@b-stock/fe-core/bstock-next/hooks'

import { redirectToAuth } from '@/helpers/auth'

// Authenticated buyer areas. When the access token is cleared — by signing out in
// this tab OR another tab (fe-core's ManagedAccessToken broadcasts logout across
// tabs via BroadcastChannel, flipping the decoded token to null everywhere) —
// every open tab on these pages redirects to sign-in, matching accounts-portal
// (BP-2062). Without this, an idle migrated My Account tab would keep its now-
// unauthenticated queries mounted and surface the 500 error boundary instead.
const PROTECTED_PREFIXES = [
  '/buy/my-account',
  '/user/',
  '/buy/auction-schedule',
]

// Person-scoped routes (/buy/p/<personId>/...) can't be listed as a plain
// string prefix since personId varies, so they get their own patterns here.
// Only Watchlist belongs — the sibling Saved Searches route under the same
// /buy/p/<personId>/ shape was reviewed and confirmed to degrade to its
// signed-out empty state on purpose, so it must NOT match.
const PROTECTED_PATTERNS = [/^\/buy\/p\/[^/]+\/watchlist(\/|$)/]

// Small delay so a transient null during an in-flight token refresh doesn't
// trigger a spurious redirect; a real sign-out leaves the token null, so the
// redirect still fires.
const REDIRECT_DELAY_MS = 750

const isProtectedPath = (pathname: string | null): boolean =>
  !!pathname &&
  (PROTECTED_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    PROTECTED_PATTERNS.some((pattern) => pattern.test(pathname)))

/**
 * Redirects open buyer-account tabs to sign-in when the access token is cleared,
 * including sign-outs that happen in another tab. Renders nothing.
 */
export const CrossTabAuthRedirect = () => {
  const decodedAccessToken = useDecodedAccessToken()
  const pathname = usePathname()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const clearPending = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    // Clear personalization cache whenever the token is absent — covers public
    // pages where the early-return below would otherwise skip it, preventing
    // cross-account cache contamination on same-tab re-login.
    if (!decodedAccessToken) {
      clearPersonalizationCache()
    }

    // Token present, or we've left the buyer-account area → cancel any pending
    // redirect.
    if (decodedAccessToken || !isProtectedPath(pathname)) {
      clearPending()
      return
    }

    // Navigating between protected pages must NOT restart the countdown, so only
    // start a timer when one isn't already pending.
    if (timerRef.current) return
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      redirectToAuth()
    }, REDIRECT_DELAY_MS)
  }, [decodedAccessToken, pathname])

  // Cancel a pending redirect on unmount.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  return null
}
